import { SetAssetPracticeTask } from "./SetAssetPracticeTask.js";
import { Link } from "react-router-dom";
import { formatDate } from "./format.js";
import { grooveSnapshotHref, groovesForStudent } from "./grooves.js";
import { useAsync } from "./useAsync.js";

/**
 * The grooves set for one student, on their record.
 *
 * Live shares only. A withdrawn one is kept in the database and is a question
 * for the audit log, not something a tutor scanning a record before a lesson
 * needs in the way.
 *
 * The route behind this refuses anybody who is not an owner, this child's
 * family, or a tutor assigned to teach them — so the panel is shown to whoever
 * can reach the record and says nothing when there is nothing.
 */

export function StudentGrooves({ studentId, onPracticeChanged }: { studentId: string; onPracticeChanged?: () => void }) {
  const { data, error, loading } = useAsync(() => groovesForStudent(studentId), [studentId]);

  if (loading || error) return null;

  const shared = data?.shared ?? [];
  if (shared.length === 0) return null;

  return (
    <section className="panel">
      <h2>Grooves</h2>
      <ul className="groove-list">
        {shared.map((entry) => (
          <li key={entry.shareId}>
            <Link to={grooveSnapshotHref(entry.token)}>{entry.groove.title}</Link>
            <SetAssetPracticeTask target={{ domain: "private", learnerId: studentId }} source={{ kind: "groove_share", id: entry.shareId }}
              assetTitle={entry.groove.title} onSaved={onPracticeChanged} />
            {entry.note && <span className="note">“{entry.note}”</span>}
            <span className="meta">
              {entry.sharedBy ?? "the school"}, {formatDate(entry.sharedAt)}
            </span>
            {/* Whether it was set as work, and whether it came back done —
                the two things a tutor wants before a lesson, which the share
                on its own does not say. */}
            {entry.setAsPractice && (
              <span className={entry.practiceDone ? "badge done" : "badge"}>
                {entry.practiceDone ? "practised" : "set as practice"}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
